import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface DayOneDBProject {
  id: string;
  project_id: string;
  supabase_url: string | null;
  supabase_project_id: string | null;
  status: "provisioning" | "active" | "failed" | "suspended";
  error_message: string | null;
  region: string;
  created_at: string;
}

export interface DayOneDBColumn {
  name: string;
  type: string;
  nullable: boolean;
  default_value: string | null;
}

export interface DayOneDBTable {
  name: string;
  columns: DayOneDBColumn[];
  row_count: number;
  rls_enabled: boolean;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

const POLL_INTERVAL = 5_000; // 5 seconds
const MAX_POLL_DURATION = 120_000; // 2 minutes

export function useDayOneDB(projectId: string | null) {
  const [provisionedProject, setProvisionedProject] = useState<DayOneDBProject | null>(null);
  const [tables, setTables] = useState<DayOneDBTable[]>([]);
  const [tableData, setTableData] = useState<Record<string, unknown>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [applyingSchema, setApplyingSchema] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollStartRef = useRef<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- Fetch provisioned project row ---- */
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await (supabase as any)
        .from("user_projects")
        .select("id, project_id, supabase_url, supabase_project_id, status, error_message, region, created_at")
        .eq("project_id", projectId)
        .maybeSingle();

      if (err) throw new Error(err.message);
      setProvisionedProject(data as DayOneDBProject | null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to fetch project");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /* ---- Provision a new database ---- */
  const provision = useCallback(async () => {
    if (!projectId) return;
    setProvisioning(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("provision-project", {
        body: { projectId },
      });
      if (err) throw new Error(err.message);
      if (data?.error) throw new Error(data.error);
      // Re-fetch to pick up the new row
      await fetchProject();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Provisioning failed");
    } finally {
      setProvisioning(false);
    }
  }, [projectId, fetchProject]);

  /* ---- Apply schema SQL ---- */
  const applySchema = useCallback(
    async (sql: string, seedSql?: string) => {
      if (!projectId) return;
      setApplyingSchema(true);
      setError(null);
      try {
        const { data, error: err } = await supabase.functions.invoke("apply-schema", {
          body: { projectId, sql, seedSql },
        });
        if (err) throw new Error(err.message);
        if (data?.error) throw new Error(data.error);
        return data;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Schema apply failed";
        setError(msg);
        throw e;
      } finally {
        setApplyingSchema(false);
      }
    },
    [projectId],
  );

  /* ---- Fetch tables via apply-schema "list-tables" action ---- */
  const fetchTables = useCallback(async () => {
    if (!projectId || provisionedProject?.status !== "active") return;
    setTablesLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("apply-schema", {
        body: { projectId, action: "list-tables" },
      });
      if (err) throw new Error(err.message);
      if (data?.error) throw new Error(data.error);
      setTables((data?.tables as DayOneDBTable[]) ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to fetch tables");
    } finally {
      setTablesLoading(false);
    }
  }, [projectId, provisionedProject?.status]);

  /* ---- Fetch rows from a specific table ---- */
  const fetchTableData = useCallback(
    async (tableName: string, limit = 10) => {
      if (!projectId || provisionedProject?.status !== "active") return;
      setError(null);
      try {
        const { data, error: err } = await supabase.functions.invoke("apply-schema", {
          body: {
            projectId,
            action: "query",
            sql: `SELECT * FROM "${tableName}" LIMIT ${limit}`,
          },
        });
        if (err) throw new Error(err.message);
        if (data?.error) throw new Error(data.error);
        setTableData((data?.rows as Record<string, unknown>[]) ?? []);
        return data?.rows;
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to fetch table data");
        return null;
      }
    },
    [projectId, provisionedProject?.status],
  );

  /* ---- Fetch on mount ---- */
  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  /* ---- Poll while provisioning ---- */
  useEffect(() => {
    if (provisionedProject?.status !== "provisioning") {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    if (pollStartRef.current === 0) {
      pollStartRef.current = Date.now();
    }

    const poll = () => {
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION) {
        setError("Provisioning is taking longer than expected. Please check back later.");
        pollStartRef.current = 0;
        return;
      }
      pollTimerRef.current = setTimeout(async () => {
        await fetchProject();
        poll();
      }, POLL_INTERVAL);
    };

    poll();

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [provisionedProject?.status, fetchProject]);

  /* ---- Reset poll timer when status leaves provisioning ---- */
  useEffect(() => {
    if (provisionedProject?.status === "active") {
      pollStartRef.current = 0;
    }
  }, [provisionedProject?.status]);

  /* ---- Auto-fetch tables when active ---- */
  useEffect(() => {
    if (provisionedProject?.status === "active") {
      fetchTables();
    }
  }, [provisionedProject?.status, fetchTables]);

  return {
    provisionedProject,
    tables,
    tableData,
    loading,
    tablesLoading,
    provisioning,
    applyingSchema,
    error,
    provision,
    applySchema,
    fetchProject,
    fetchTables,
    fetchTableData,
  };
}
